import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import {
	TICKET_ATTACHMENT_MAX_MB,
	TICKET_COMMENT_ATTACHMENT_ACCEPT,
	TICKET_COMMENT_ATTACHMENT_MAX
} from '../../../../shared/const'

import Btn from '../../../../UI/Btn'
import { RichTextEditor } from '../../../../UI/RichText'
import { hatInhalt } from '../../../../shared/richText'

import styles from './detail.module.scss'

const ERLAUBT = TICKET_COMMENT_ATTACHMENT_ACCEPT.split(',')

/**
 * Trennt eine Auswahl in das, was an einen Kommentar darf, und den Rest.
 *
 * Dieselbe Prüfung für alle drei Wege — Datei wählen, hineinziehen, aus der
 * Zwischenablage einfügen. Der Server prüft noch einmal; das hier erspart den
 * Weg über die Leitung, wenn schon der Typ nicht passt.
 *
 * `vorhanden` ist die Zahl der bereits angehängten Dateien: Die Höchstzahl gilt
 * für den ganzen Beitrag, nicht für eine einzelne Auswahl.
 *
 * @returns {[File[], string[]]} zulässige Dateien, Gründe für die Ablehnung
 */
export const pruefeKommentarAnhaenge = (files, vorhanden = 0) => {
	const zulaessig = []
	const abgelehnt = []
	let platz = TICKET_COMMENT_ATTACHMENT_MAX - vorhanden

	for (const file of Array.from(files || [])) {
		const name = file.name || 'Datei'
		if (!ERLAUBT.includes(file.type)) {
			abgelehnt.push(`${name}: nur PNG, JPEG und PDF`)
		} else if (file.size > TICKET_ATTACHMENT_MAX_MB * 1024 * 1024) {
			const mb = (file.size / 1024 / 1024).toFixed(1)
			abgelehnt.push(`${name}: ${mb} MB, erlaubt sind ${TICKET_ATTACHMENT_MAX_MB} MB`)
		} else if (platz <= 0) {
			abgelehnt.push(`${name}: höchstens ${TICKET_COMMENT_ATTACHMENT_MAX} Anhänge je Kommentar`)
		} else {
			zulaessig.push(file)
			platz -= 1
		}
	}

	return [zulaessig, abgelehnt]
}

/**
 * Vorschau einer noch nicht abgeschickten Datei.
 *
 * Bilder zeigen sich selbst — bei drei Bildschirmfotos hintereinander sagt der
 * Dateiname („Bildschirmfoto 2026-08-20 um 10.37.12.png") sonst nichts darüber,
 * welches davon man gerade wieder entfernen will. Die lokale Adresse wird beim
 * Abräumen wieder freigegeben, sonst behält der Browser die Daten im Speicher.
 */
function Vorschau({ file }) {
	const [url, setUrl] = useState(null)

	useEffect(() => {
		if (!file.type.startsWith('image/')) return undefined

		const objectUrl = URL.createObjectURL(file)
		setUrl(objectUrl)
		return () => URL.revokeObjectURL(objectUrl)
	}, [file])

	if (url) return <img className={styles.queueThumb} src={url} alt="" />

	return (
		<span className={styles.queueBadge} aria-hidden="true">
			PDF
		</span>
	)
}

Vorschau.propTypes = {
	file: PropTypes.object.isRequired
}

/**
 * Das Eingabefeld für einen Kommentar — mit Anhängen.
 *
 * Geschrieben wird mit den vier Grundauszeichnungen (`UI/RichText`) — fett,
 * kursiv, unterstrichen, Aufzählung. Vorher tippten Leute Sternchen um ihre
 * Hervorhebungen; die standen dann als Sternchen da.
 *
 * Jede vorgemerkte Datei bekommt ein Feld für einen Satz dazu: was das ist und
 * was darauf zu sehen ist. Ein Bildschirmfoto ohne diesen Satz ist später
 * stumm — beim Nachlesen wie bei jeder maschinellen Auswertung.
 *
 * Der Knopf sitzt unter dem Feld, nicht daneben: Daneben nahm er dem Textfeld
 * die halbe Breite einer Spalte, und ein Kommentar ist meist mehr als ein
 * Halbsatz. So ist das Feld so breit wie das Fenster und „Senden" steht dort,
 * wo man nach dem Schreiben ohnehin ankommt — unten rechts.
 *
 * Bewusst eine Komponente auf Dateiebene und keine im Inneren von
 * `TicketDetail`: Eine dort erklärte Komponente wäre bei jedem Tastendruck ein
 * neuer Typ — React würde das Feld aushängen und neu einhängen, und die
 * Schreibmarke wäre nach dem ersten Buchstaben weg.
 */
export default function CommentComposer(props) {
	const {
		value,
		onChange,
		files,
		onFiles,
		onRemoveFile,
		onDescribeFile,
		onSend,
		hasReporter,
		isPosting,
		className
	} = props

	const input = useRef(null)
	const [ueber, setUeber] = useState(false)
	const [abgelehnt, setAbgelehnt] = useState([])

	const nimm = auswahl => {
		if (!auswahl?.length) return

		const [zulaessig, gruende] = pruefeKommentarAnhaenge(auswahl, files.length)
		setAbgelehnt(gruende)
		if (zulaessig.length) onFiles(zulaessig)
	}

	const voll = files.length >= TICKET_COMMENT_ATTACHMENT_MAX

	return (
		<div className={clsx(styles.commentForm, className)}>
			<div
				className={clsx(styles.composer, ueber && styles.composerOver)}
				onDragOver={event => {
					// Ohne preventDefault öffnet der Browser die Datei in einem
					// neuen Reiter, statt sie hier abzulegen.
					event.preventDefault()
					setUeber(true)
				}}
				onDragLeave={() => setUeber(false)}
				onDrop={event => {
					event.preventDefault()
					setUeber(false)
					nimm(event.dataTransfer?.files)
				}}
			>
				<RichTextEditor
					flach
					className={styles.composerInput}
					value={value}
					onChange={onChange}
					disabled={isPosting}
					placeholder="Rückfrage, Entscheidung oder Zwischenstand …"
					ariaLabel="Kommentar"
					onPaste={event => {
						// Ein Bildschirmfoto liegt als Datei in der Zwischenablage.
						// Nur dann eingreifen — sonst ließe sich kein Text mehr
						// einfügen; um den kümmert sich das Feld selbst.
						const dateien = Array.from(event.clipboardData?.files || [])
						if (!dateien.length) return

						event.preventDefault()
						nimm(dateien)
					}}
					onKeyDown={event => {
						// Strg+Enter schickt ab. Enter allein bleibt ein
						// Zeilenumbruch - man schreibt hier mehrzeilig.
						if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
							event.preventDefault()
							onSend()
						}
					}}
				/>

				{Boolean(files.length) && (
					<ul className={styles.queue}>
						{files.map((eintrag, index) => (
							<li
								key={`${eintrag.file.name}-${eintrag.file.size}-${index}`}
								className={styles.queueItem}
							>
								<Vorschau file={eintrag.file} />
								<div className={styles.queueBody}>
									<span className={styles.queueName} title={eintrag.file.name}>
										{eintrag.file.name}
									</span>
									{/*
									  * Der Satz zur Datei. Kein Pflichtfeld — wer ihn
									  * weglässt, kommt trotzdem durch; erzwungen wäre er
									  * in zwei Wochen mit „x" gefüllt.
									  */}
									<input
										type="text"
										className={styles.queueDescription}
										value={eintrag.description}
										maxLength={2000}
										placeholder="Was ist darauf zu sehen? (hilft beim späteren Nachlesen)"
										aria-label={`Beschreibung zu ${eintrag.file.name}`}
										onChange={event => onDescribeFile(index, event.target.value)}
									/>
								</div>
								<button
									type="button"
									className={styles.queueRemove}
									aria-label={`${eintrag.file.name} entfernen`}
									title="Entfernen"
									onClick={() => onRemoveFile(index)}
								>
									×
								</button>
							</li>
						))}
					</ul>
				)}

				<div className={styles.composerBar}>
					<div className={styles.composerTools}>
						<button
							type="button"
							className={styles.attachBtn}
							disabled={isPosting || voll}
							title={
								voll
									? `Mehr als ${TICKET_COMMENT_ATTACHMENT_MAX} Anhänge sind nicht möglich`
									: 'Bild oder PDF anhängen'
							}
							onClick={() => input.current?.click()}
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								strokeLinecap="round"
								strokeLinejoin="round"
								aria-hidden="true"
							>
								<path d="M21.44 11.05l-8.49 8.49a5 5 0 0 1-7.07-7.07l8.49-8.49a3.5 3.5 0 0 1 4.95 4.95l-8.49 8.49a2 2 0 0 1-2.83-2.83l7.78-7.78" />
							</svg>
							Anhang
						</button>
						<span className={styles.composerCount}>
							{files.length}/{TICKET_COMMENT_ATTACHMENT_MAX} · PNG, JPEG, PDF bis{' '}
							{TICKET_ATTACHMENT_MAX_MB} MB · ziehen oder mit Strg+V einfügen
						</span>
					</div>
				</div>

				{/*
				 * Die drei Knöpfe stehen in einer **eigenen Zeile** unter der
				 * Werkzeugleiste: Nebeneinander mit Anhang und Hinweis brachen sie
				 * je nach Fensterbreite um, und „Rückfrage" rutschte allein in die
				 * nächste Zeile — was aussah, als gehöre es nicht dazu.
				 *
				 * Drei Knöpfe statt eines Auswahlfelds „Sichtbarkeit": Ein Feld
				 * daneben hieße, dass die Vorgabe eine Einstellung ist, die man
				 * übersieht. So ist jede Sichtbarkeit eine eigene Handlung mit
				 * eigenem Wort, und „Intern vermerken" steht vorne.
				 *
				 * `hatInhalt` statt `value.trim()`: Ein leer geräumtes Feld
				 * hinterlässt `<p><br></p>` — für den Browser Inhalt, für die
				 * Leserin nichts.
				 */}
				<div className={styles.composerActions}>
					<Btn
						size="small"
						theme="primary"
						disabled={isPosting || !hatInhalt(value)}
						onClick={() => onSend()}
					>
						{isPosting ? 'Wird gesendet …' : hasReporter ? 'Intern vermerken' : 'Senden'}
					</Btn>

					{hasReporter && (
						<>
							<Btn
								size="small"
								theme="success"
								disabled={isPosting || !hatInhalt(value)}
								onClick={() => onSend({ visibility: 'REPORTER' })}
							>
								Antworten
							</Btn>
							<Btn
								size="small"
								theme="orange"
								disabled={isPosting || !hatInhalt(value)}
								onClick={() => onSend({ visibility: 'REPORTER', expectsAnswer: true })}
							>
								Rückfrage
							</Btn>
						</>
					)}
				</div>

				<input
					ref={input}
					type="file"
					accept={TICKET_COMMENT_ATTACHMENT_ACCEPT}
					multiple
					className={styles.hiddenInput}
					onChange={event => {
						nimm(event.target.files)
						// Zuruecksetzen, sonst laesst sich dieselbe Datei nicht ein
						// zweites Mal waehlen - der Wert aendert sich dann nicht.
						event.target.value = ''
					}}
				/>
			</div>

			{Boolean(abgelehnt.length) && (
				<ul className={styles.rejected}>
					{abgelehnt.map(grund => (
						<li key={grund}>{grund}</li>
					))}
				</ul>
			)}

			<span className={styles.hint}>
				Strg+Enter bzw. Cmd+Enter vermerkt intern
				{hasReporter
					? ' · „Antworten" und „Rückfrage" gehen an die meldende Person'
					: ' · Kommentare bleiben intern'}
			</span>
		</div>
	)
}

CommentComposer.propTypes = {
	value: PropTypes.string.isRequired,
	onChange: PropTypes.func.isRequired,
	// Datei plus der Satz dazu — die Beschreibung geht erst mit dem Senden an den
	// Server, zusammen mit ihrer Datei.
	files: PropTypes.arrayOf(
		PropTypes.shape({ file: PropTypes.object.isRequired, description: PropTypes.string })
	).isRequired,
	onFiles: PropTypes.func.isRequired,
	onRemoveFile: PropTypes.func.isRequired,
	onDescribeFile: PropTypes.func.isRequired,
	// `onSend()` vermerkt intern, `onSend({visibility, expectsAnswer})` schickt
	// den Beitrag an die meldende Person.
	onSend: PropTypes.func.isRequired,
	// Gibt es überhaupt jemanden, dem etwas sichtbar wäre? Bei einer intern
	// erfassten Anforderung nicht — dann bleibt es beim einen Knopf.
	hasReporter: PropTypes.bool,
	isPosting: PropTypes.bool.isRequired,
	className: PropTypes.string
}

CommentComposer.defaultProps = {
	className: '',
	hasReporter: false
}
